import { Link } from "react-router-dom";
import { ArrowRight, Clock } from "lucide-react";
import { Img } from "@/components/common/Primitives";
import { useContent, minPrice } from "@/lib/content";
import { useLang } from "@/lib/i18n";
import { nok } from "@/lib/format";

/** Nav items that open a photo preview panel on hover / focus (desktop only) */
export const PANEL_IDS = ["rooms", "dining", "explore"];

const TILE_SIZES = "(min-width: 1024px) 220px, 40vw";

const Tile = ({ to, img, title, sub, position, testid, onNavigate, aspect = "aspect-[4/3]" }) => (
  <Link to={to} onClick={onNavigate} className="group/tile block rounded-2xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2" data-testid={testid}>
    <Img src={img} alt="" className={`img-zoom ${aspect}`} radius="rounded-2xl" sizes={TILE_SIZES} position={position} reveal={false} eager />
    <p className="mt-2.5 text-sm font-semibold leading-snug text-foreground">{title}</p>
    {sub && <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground tabular">{sub}</p>}
  </Link>
);

const Shell = ({ eyebrow, title, intro, to, linkLabel, onNavigate, children, testid }) => (
  <div className="grid grid-cols-[220px_minmax(0,1fr)] gap-8" data-testid={testid}>
    <div className="flex flex-col">
      <p className="text-xs font-semibold text-muted-foreground">{eyebrow}</p>
      <p className="mt-2 text-[1.375rem] font-semibold leading-tight tracking-[-0.02em] text-foreground">{title}</p>
      <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{intro}</p>
      <Link to={to} onClick={onNavigate} className="group/link mt-auto inline-flex items-center gap-1.5 pt-5 text-sm font-semibold text-foreground" data-testid={`${testid}-link`}>
        {linkLabel} <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover/link:translate-x-0.5" />
      </Link>
    </div>
    {children}
  </div>
);

export const NavPanel = ({ id, onNavigate }) => {
  const { lang, tx, tr } = useLang();
  const { data } = useContent();

  if (id === "rooms") {
    const rooms = (data?.room_types || []).filter((r) => r.active !== false).sort((a, b) => (a.sort || 0) - (b.sort || 0)).slice(0, 5);
    return (
      <Shell
        testid="nav-panel-rooms"
        eyebrow={tx("51 rom · 115 sengeplassar", "51 rooms · 115 beds")}
        title={tx("Rom ved fjorden", "Rooms by the fjord")}
        intro={tx("Frukost inkludert i alle rom. Betal når du kjem.", "Breakfast included in every room. Pay on arrival.")}
        to="/rom"
        linkLabel={tx("Alle rom og prisar", "All rooms and rates")}
        onNavigate={onNavigate}
      >
        <div className="grid grid-cols-5 gap-4">
          {rooms.map((r) => (
            <Tile key={r.id} to={`/rom/${r.slug}`} img={r.images?.[0]} title={tr(r.name)} sub={`${tx("frå", "from")} ${nok(minPrice(r), lang)}`} testid={`nav-panel-room-${r.id}`} onNavigate={onNavigate} />
          ))}
        </div>
      </Shell>
    );
  }

  if (id === "explore") {
    const acts = [...(data?.activities || [])].sort((a, b) => (a.sort || 0) - (b.sort || 0)).slice(0, 6);
    return (
      <Shell
        testid="nav-panel-explore"
        eyebrow={tx("Ved fjorden og i fjellet", "By the fjord and in the mountains")}
        title={tx("Opplev Skånevik", "Explore Skånevik")}
        intro={tx("Basseng i hagen, badeøy fem minutt unna og fjellet rett bak bygda.", "A pool in the garden, a bathing island five minutes away and mountains right behind the village.")}
        to="/opplev"
        linkLabel={tx("Alt du kan oppleve", "Everything to experience")}
        onNavigate={onNavigate}
      >
        <div className="grid grid-cols-6 gap-4">
          {acts.map((a) => (
            <Tile key={a.id} to={`/opplev#${a.id}`} img={a.images?.[0]} title={tr(a.title)} sub={tr(a.kicker)} position={a.id === "peparen" ? "50% 60%" : "50% 50%"} testid={`nav-panel-activity-${a.id}`} onNavigate={onNavigate} aspect="aspect-[4/5]" />
          ))}
        </div>
      </Shell>
    );
  }

  if (id === "dining") {
    const hours = Object.fromEntries((data?.dining?.hours || []).map((h) => [h.key, h.value]));
    const tiles = [
      { to: "/mat", img: "/images/restaurant-window.jpg", title: tx("Restauranten", "The restaurant"), sub: tx(`Frukost ${hours.breakfast || "07.30–10.00"} · middag ${hours.dinner || "17.00–21.00"}`, `Breakfast ${hours.breakfast || "07.30–10.00"} · dinner ${hours.dinner || "17.00–21.00"}`), position: "50% 40%", id: "restaurant" },
      { to: "/mat#meny", img: "/images/cafe-chalk-counter.jpg", title: "Strandkaféen", sub: tx("Steinomnspizza på kaien · 7 pizzaer, 299 kr", "Stone-oven pizza on the quay · 7 pizzas, NOK 299"), position: "40% 50%", id: "cafe" },
      { to: "/mat#opningstider", img: "/images/flyer-komle.jpg", title: tx("Komletorsdag", "Komle Thursday"), sub: tx("Kvar torsdag frå kl. 13", "Every Thursday from 13:00"), position: "50% 50%", id: "komle" },
      { to: "/mat#bord", img: "/images/restaurant-hall.jpg", title: tx("Bestill bord", "Book a table"), sub: tx("Via Fjordverten eller telefon", "Via Fjordverten or by phone"), position: "50% 60%", id: "table" },
    ];
    return (
      <Shell
        testid="nav-panel-dining"
        eyebrow={tx("Restauranten · Strandkaféen", "The restaurant · Strandkaféen")}
        title={tx("Mat ved kaien", "Food on the quay")}
        intro={tx("Frukost, middag og søndagsmiddag i restauranten – pizza, kaffi og is på kaien.", "Breakfast, dinner and Sunday dinner in the restaurant – pizza, coffee and ice cream on the quay.")}
        to="/mat"
        linkLabel={tx("Menyar og opningstider", "Menus and opening hours")}
        onNavigate={onNavigate}
      >
        <div className="grid grid-cols-4 gap-4">
          {tiles.map((t) => <Tile key={t.id} {...t} testid={`nav-panel-dining-${t.id}`} onNavigate={onNavigate} />)}
        </div>
      </Shell>
    );
  }
  return null;
};

export const PanelHint = ({ children }) => (
  <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground"><Clock className="h-3 w-3" /> {children}</span>
);
